import React, { useState, useEffect, useCallback } from "react";
import { Card, Table, Tag, Button, Space, Tooltip, Popconfirm, Typography, message, theme } from "antd";
import { ReloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { authenticatedFetch } from "@/libs/api";
import {
  STATUS_META, TARGET_LABELS, MODE_META, RETRYABLE, retrySyncTask, retryConfirmText, formatTime,
} from "./syncStatus";

const { Text } = Typography;

/**
 * System Detail → what this system's approvals set off downstream (the customer's Powow APP
 * account, for now). Superadmin only — the page renders it only for SUPERADMIN, and the
 * endpoints refuse anyone else.
 */
export default function DownstreamSyncSection({ systemId }) {
  const { token } = theme.useToken();
  const [data, setData] = useState({ tasks: [], targets: [] });
  const [loading, setLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!systemId) return;
    setLoading(true);
    try {
      const res = await authenticatedFetch(`/api/downstream-sync/systems/${encodeURIComponent(systemId)}`);
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (err) {
      console.error("Error fetching downstream sync:", err);
    } finally {
      setLoading(false);
    }
  }, [systemId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRetry = async (task) => {
    setRetryingId(task.id);
    try {
      const result = await retrySyncTask(task.id);
      message.success(`Retried: now ${STATUS_META[result.to]?.label || result.to}`);
      fetchData();
    } catch (err) {
      message.error(err.message);
    } finally {
      setRetryingId(null);
    }
  };

  const columns = [
    {
      title: "Downstream",
      dataIndex: "target",
      key: "target",
      width: 150,
      render: (t) => TARGET_LABELS[t] || t,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (s) => (
        <Tooltip title={STATUS_META[s]?.help}>
          <Tag color={STATUS_META[s]?.color}>{STATUS_META[s]?.label || s}</Tag>
        </Tooltip>
      ),
    },
    {
      title: "Detail",
      key: "detail",
      render: (_, r) => {
        if (r.last_error) return <Text style={{ fontSize: 12 }}>{r.last_error}</Text>;
        if (r.status === "DONE") return <Text style={{ fontSize: 12 }}>APP user id {r.external_id}</Text>;
        return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
      },
    },
    {
      title: "Approved",
      dataIndex: "approval_at",
      key: "approval_at",
      width: 160,
      render: (v) => <Text style={{ fontSize: 12 }}>{formatTime(v)}</Text>,
    },
    {
      title: "Updated",
      dataIndex: "update_time",
      key: "update_time",
      width: 160,
      render: (v, r) => (
        <Text style={{ fontSize: 12 }}>
          {formatTime(v)}{r.attempts > 0 ? ` · ${r.attempts} attempt${r.attempts > 1 ? "s" : ""}` : ""}
        </Text>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 90,
      render: (_, r) => (RETRYABLE.includes(r.status) ? (
        <Popconfirm
          title="Retry this sync?"
          description={<div style={{ maxWidth: 280 }}>{retryConfirmText(r)}</div>}
          onConfirm={() => handleRetry(r)}
          okText="Retry"
        >
          <Button size="small" loading={retryingId === r.id}>Retry</Button>
        </Popconfirm>
      ) : null),
    },
  ];

  return (
    <Card
      size="small"
      style={{ marginTop: 16 }}
      title={(
        <Space>
          <span>Onboard Sync</span>
          <Tooltip title="What an approval sets off outside the platform. Admin only.">
            <InfoCircleOutlined style={{ color: token.colorTextSecondary }} />
          </Tooltip>
          {data.targets.map((t) => (
            <Tag key={t.target} color={t.paused ? "error" : MODE_META[t.mode]?.color}>
              {TARGET_LABELS[t.target] || t.target}: {t.paused ? "Paused" : MODE_META[t.mode]?.label || t.mode}
            </Tag>
          ))}
        </Space>
      )}
      extra={<Button size="small" icon={<ReloadOutlined spin={loading} />} onClick={fetchData} />}
    >
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data.tasks}
        pagination={false}
        locale={{ emptyText: "Nothing has been queued for this system (it has not been approved since sync was added)." }}
        expandable={{
          rowExpandable: (r) => Boolean(r.request_summary),
          expandedRowRender: (r) => (
            <div style={{ fontSize: 12 }}>
              <Text type="secondary">
                {{ DONE: "Sent:", SHADOW: "Would send:", PENDING: "Will send:", SENDING: "Sending:" }[r.status] || "Built, not sent:"}
              </Text>
              <pre style={{ margin: "4px 0 0", fontSize: 12, whiteSpace: "pre-wrap" }}>
                {JSON.stringify(r.request_summary, null, 2)}
              </pre>
            </div>
          ),
        }}
      />
    </Card>
  );
}
